'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { SERVICES } from '@/lib/constants';
import { nameToSlug } from '@/lib/country-utils';
import { CountryFlag } from '@/components/ui/country-flag';

interface DirectoryResult {
  id: string;
  company_name: string;
  city: string | null;
  country_code: string;
  country_name: string;
  slug: string | null;
  services: string[];
  certifications: string[];
  networks: string[];
  logo_url: string | null;
  headline: string | null;
  forwarder_type: string;
  avg_response_time_hours: number | null;
  ranking_score: number | null;
  source: 'profile' | 'yellow_pages';
}

interface DirectoryClientProps {
  countries: Array<{ code: string; name: string; count: number }>;
  serviceTypes: Array<{ id: string; name: string }>;
}

function getServiceLabel(serviceId: string): string {
  const service = SERVICES.find(s => s.id === serviceId);
  return service?.name || serviceId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

function getForwarderTypeLabel(type: string): string {
  switch (type) {
    case 'ranked': return 'Ranked';
    case 'paid_unranked': return 'Featured';
    case 'unpaid_unranked': return 'Verified';
    case 'yellow_pages': return 'Directory';
    default: return type;
  }
}

function formatResponseTime(hours: number | null): string {
  if (hours === null || hours === undefined) return '--';
  if (hours < 1) return `${Math.round(hours * 60)}min`;
  if (hours < 24) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

export function DirectoryClient({ countries, serviceTypes }: DirectoryClientProps) {
  const router = useRouter();
  const [forwarderQuery, setForwarderQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [results, setResults] = useState<DirectoryResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [serviceDropdownOpen, setServiceDropdownOpen] = useState(false);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const serviceDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setCountryDropdownOpen(false);
      }
      if (serviceDropdownRef.current && !serviceDropdownRef.current.contains(event.target as Node)) {
        setServiceDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Determine if we should show a "Go to Country" button vs direct results
  // Company name search -> show results directly
  // Country/service only (no company name) -> show button to navigate to country page
  const isCompanySearch = forwarderQuery.trim().length > 0;
  const hasCountrySelected = selectedCountry !== '';

  // Generate country slug from name using shared utility
  const countryName = countries.find(c => c.code === selectedCountry)?.name;
  const effectiveSlug = countryName ? nameToSlug(countryName) : null;

  // Build the country page URL with optional service filter
  const getCountryPageUrl = () => {
    if (!effectiveSlug) return null;
    const base = `/${effectiveSlug}`;
    if (selectedService) {
      return `${base}?service=${selectedService}`;
    }
    return base;
  };

  const handleSearchNavigation = () => {
    const url = getCountryPageUrl();
    if (url) {
      router.push(url);
    }
  };

  // Only do API search when there's a company name query
  const search = useCallback(async () => {
    if (!forwarderQuery.trim()) {
      setResults([]);
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setHasSearched(true);

    const params = new URLSearchParams();
    params.set('q', forwarderQuery.trim());
    if (selectedCountry) params.set('country', selectedCountry);
    if (selectedService) params.set('service', selectedService);

    try {
      const res = await fetch(`/api/directory/search?${params.toString()}`);
      const data = await res.json();
      setResults(data.results || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [forwarderQuery, selectedCountry, selectedService]);

  // Debounced search on input change (only for company name searches)
  useEffect(() => {
    const timer = setTimeout(() => {
      search();
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Filter countries for dropdown
  const filteredCountries = countries.filter(c =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    c.code.toLowerCase().includes(countrySearch.toLowerCase())
  );

  const selectedCountryData = countries.find(c => c.code === selectedCountry);
  const selectedServiceData = serviceTypes.find(s => s.id === selectedService);

  return (
    <>
      {/* Search Filters Section */}
      <section className="directory-search-section">
        <div className="directory-search-grid">
          {/* Search by Forwarder */}
          <div className="directory-search-field">
            <label className="directory-field-label">Search Forwarder</label>
            <div className="directory-input-wrapper">
              <svg className="directory-search-icon" viewBox="0 0 24 24" fill="none" width="16" height="16">
                <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="1.5" />
                <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" />
              </svg>
              <input
                type="text"
                className="directory-search-input"
                placeholder="Company name..."
                value={forwarderQuery}
                onChange={(e) => setForwarderQuery(e.target.value)}
              />
              {forwarderQuery && (
                <button
                  className="directory-clear-btn"
                  onClick={() => setForwarderQuery('')}
                  aria-label="Clear search"
                >
                  <svg viewBox="0 0 24 24" fill="none" width="14" height="14">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Search by Country */}
          <div className="directory-search-field" ref={countryDropdownRef}>
            <label className="directory-field-label">Country</label>
            <div className="directory-dropdown-wrapper">
              <button
                type="button"
                className={`directory-dropdown-trigger ${countryDropdownOpen ? 'open' : ''}`}
                onClick={() => {
                  setCountryDropdownOpen(!countryDropdownOpen);
                  setServiceDropdownOpen(false);
                }}
              >
                <span className={selectedCountry ? '' : 'directory-placeholder'}>
                  {selectedCountry
                    ? <><CountryFlag code={selectedCountry} size={18} /> {selectedCountryData?.name || selectedCountry}</>
                    : 'All Countries'}
                </span>
                <svg className={`directory-dropdown-arrow ${countryDropdownOpen ? 'rotated' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" />
                </svg>
              </button>
              {countryDropdownOpen && (
                <div className="directory-dropdown-menu">
                  <div className="directory-dropdown-search">
                    <input
                      type="text"
                      placeholder="Search countries..."
                      value={countrySearch}
                      onChange={(e) => setCountrySearch(e.target.value)}
                      className="directory-dropdown-search-input"
                      autoFocus
                    />
                  </div>
                  <div className="directory-dropdown-options">
                    <button
                      type="button"
                      className={`directory-dropdown-option ${!selectedCountry ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedCountry('');
                        setCountryDropdownOpen(false);
                        setCountrySearch('');
                      }}
                    >
                      <span>All Countries</span>
                    </button>
                    {filteredCountries.map(c => (
                      <button
                        key={c.code}
                        type="button"
                        className={`directory-dropdown-option ${selectedCountry === c.code ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedCountry(c.code);
                          setCountryDropdownOpen(false);
                          setCountrySearch('');
                        }}
                      >
                        <span><CountryFlag code={c.code} size={18} /> {c.name}</span>
                        <span className="directory-dropdown-count">{c.count}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Filter by Service */}
          <div className="directory-search-field directory-search-field-last" ref={serviceDropdownRef}>
            <label className="directory-field-label">Service Type</label>
            <div className="directory-dropdown-wrapper">
              <button
                type="button"
                className={`directory-dropdown-trigger ${serviceDropdownOpen ? 'open' : ''}`}
                onClick={() => {
                  setServiceDropdownOpen(!serviceDropdownOpen);
                  setCountryDropdownOpen(false);
                }}
              >
                <span className={selectedService ? '' : 'directory-placeholder'}>
                  {selectedService
                    ? selectedServiceData?.name || selectedService
                    : 'All Services'}
                </span>
                <svg className={`directory-dropdown-arrow ${serviceDropdownOpen ? 'rotated' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" />
                </svg>
              </button>
              {serviceDropdownOpen && (
                <div className="directory-dropdown-menu">
                  <div className="directory-dropdown-options">
                    <button
                      type="button"
                      className={`directory-dropdown-option ${!selectedService ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedService('');
                        setServiceDropdownOpen(false);
                      }}
                    >
                      <span>All Services</span>
                    </button>
                    {serviceTypes.map(s => (
                      <button
                        key={s.id}
                        type="button"
                        className={`directory-dropdown-option ${selectedService === s.id ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedService(s.id);
                          setServiceDropdownOpen(false);
                        }}
                      >
                        <span>{s.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Active filters summary */}
        {(forwarderQuery || selectedCountry || selectedService) && (
          <div className="directory-active-filters">
            <span className="directory-filter-count">
              {loading ? 'Searching...' : `${results.length} result${results.length !== 1 ? 's' : ''}`}
            </span>
            <div className="directory-filter-tags">
              {forwarderQuery && (
                <button className="directory-filter-tag" onClick={() => setForwarderQuery('')}>
                  &ldquo;{forwarderQuery}&rdquo;
                  <svg viewBox="0 0 24 24" fill="none" width="12" height="12">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="square" />
                  </svg>
                </button>
              )}
              {selectedCountry && (
                <button className="directory-filter-tag" onClick={() => setSelectedCountry('')}>
                  <CountryFlag code={selectedCountry} size={16} /> {selectedCountryData?.name}
                  <svg viewBox="0 0 24 24" fill="none" width="12" height="12">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="square" />
                  </svg>
                </button>
              )}
              {selectedService && (
                <button className="directory-filter-tag" onClick={() => setSelectedService('')}>
                  {selectedServiceData?.name}
                  <svg viewBox="0 0 24 24" fill="none" width="12" height="12">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="square" />
                  </svg>
                </button>
              )}
              <button className="directory-clear-all" onClick={() => {
                setForwarderQuery('');
                setSelectedCountry('');
                setSelectedService('');
              }}>
                Clear All
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Country Search Button (when country is selected without company name) */}
      {hasCountrySelected && !isCompanySearch && (
        <section className="directory-country-action">
          <div className="directory-country-cta">
            <div className="directory-country-cta-text">
              <CountryFlag code={selectedCountry} size={48} className="directory-country-cta-flag" />
              <div>
                <h3 className="directory-country-cta-title">
                  {selectedCountryData?.name || selectedCountry}
                </h3>
                <p className="directory-country-cta-subtitle">
                  {selectedService
                    ? `View rankings and forwarders filtered by ${selectedServiceData?.name || selectedService}`
                    : `View all ranked and featured forwarders`}
                </p>
              </div>
            </div>
            {effectiveSlug ? (
              <button
                type="button"
                className="directory-search-btn"
                onClick={handleSearchNavigation}
              >
                Search
                <svg className="arrow-icon" viewBox="0 0 24 24" fill="none" width="18" height="18">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" />
                </svg>
              </button>
            ) : (
              <span className="directory-country-cta-unavailable">Country page coming soon</span>
            )}
          </div>
        </section>
      )}

      {/* Results (only for company name search) */}
      <section className="directory-results-section">
        {!hasSearched && !hasCountrySelected && (
          <div className="directory-empty-state">
            <div className="directory-empty-icon">
              <svg viewBox="0 0 64 64" fill="none" width="64" height="64">
                <circle cx="28" cy="28" r="20" stroke="currentColor" strokeWidth="2" />
                <path d="M44 44l14 14" stroke="currentColor" strokeWidth="2" strokeLinecap="square" />
              </svg>
            </div>
            <h3 className="directory-empty-title">Search the Directory</h3>
            <p className="directory-empty-text">
              Search by forwarder name, or select a country and service type to browse.
            </p>
          </div>
        )}

        {isCompanySearch && hasSearched && !loading && results.length === 0 && (
          <div className="directory-empty-state">
            <h3 className="directory-empty-title">No Results Found</h3>
            <p className="directory-empty-text">
              Try adjusting your search criteria or broadening your filters.
            </p>
          </div>
        )}

        {isCompanySearch && hasSearched && results.length > 0 && (
          <>
            {/* Results Table Header */}
            <div className="directory-results-header">
              <div className="dir-col-header dir-col-operator">Operator</div>
              <div className="dir-col-header dir-col-country">Country</div>
              <div className="dir-col-header dir-col-services">Services</div>
              <div className="dir-col-header dir-col-type">Type</div>
              <div className="dir-col-header dir-col-action"></div>
            </div>

            {/* Results Rows */}
            {results.map((result) => {
              const resultCountryName = countries.find(c => c.code === result.country_code)?.name;
              const resultCountrySlug = resultCountryName ? nameToSlug(resultCountryName) : null;
              const href = result.slug
                ? `/forwarders/${result.slug}`
                : resultCountrySlug
                  ? `/${resultCountrySlug}`
                  : '#';

              return (
                <Link key={`${result.source}-${result.id}`} href={href} className="directory-result-row">
                  <div className="dir-cell dir-cell-operator">
                    <div className="dir-operator-info">
                      {result.logo_url ? (
                        <Image
                          src={result.logo_url}
                          alt={result.company_name}
                          width={36}
                          height={36}
                          className="dir-operator-logo"
                        />
                      ) : (
                        <div className="dir-operator-logo-placeholder">
                          {result.company_name.substring(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div className="dir-operator-text">
                        <div className="dir-company-name">{result.company_name}</div>
                        <div className="dir-company-meta">
                          {result.city && <span>{result.city}</span>}
                          {result.avg_response_time_hours !== null && (
                            <span className="dir-response-time">
                              {formatResponseTime(result.avg_response_time_hours)} avg response
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="dir-cell dir-cell-country">
                    <span className="dir-country-badge">
                      <CountryFlag code={result.country_code} size={18} /> {result.country_name}
                    </span>
                  </div>
                  <div className="dir-cell dir-cell-services">
                    <div className="dir-services-tags">
                      {result.services.slice(0, 3).map(s => (
                        <span key={s} className="dir-service-tag">{getServiceLabel(s)}</span>
                      ))}
                      {result.services.length > 3 && (
                        <span className="dir-service-tag dir-service-more">+{result.services.length - 3}</span>
                      )}
                      {result.services.length === 0 && (
                        <span className="dir-no-services">--</span>
                      )}
                    </div>
                  </div>
                  <div className="dir-cell dir-cell-type">
                    <span className={`dir-type-badge dir-type-${result.forwarder_type}`}>
                      {getForwarderTypeLabel(result.forwarder_type)}
                    </span>
                    {result.ranking_score !== null && result.ranking_score > 0 && (
                      <span className="dir-score">{result.ranking_score}/100</span>
                    )}
                  </div>
                  <div className="dir-cell dir-cell-action">
                    <svg className="dir-arrow-icon" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" />
                    </svg>
                  </div>
                </Link>
              );
            })}
          </>
        )}
      </section>
    </>
  );
}
